// ---------------------------------------------------------------------------
// AgentEvaluateTab — in-place evaluation runner for a single agent.
//
// Mirrors the Eval Template page (query/data sets, graders, variables,
// concurrency + a Jobs rail), but the agent under test is *pinned* to the
// agent this tab belongs to — only the grader(s) and inputs are chosen here.
//
// There's no grader/agent-first run API: a run always resolves a template.
// So "Run evaluation" upserts a backing template (PUT — create-or-replace),
// named deterministically per agent, then queues a run against it. The Jobs
// rail lists that template's runs (filtered by the run displayName, which we
// set to the backing template name).
//
// This file owns orchestration only; the two body halves live in
// EvaluateFormColumn and EvaluateJobsRail.
// ---------------------------------------------------------------------------

import { Spinner } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../../api/HttpClientContext.js';
import { EvaluationRunsService } from '../../../../services/evaluationRunsService.js';
import { EvaluationTemplateNotFoundError } from '../../../../services/evaluationTemplatesService.interface.js';
import { EvaluationTemplatesService } from '../../../../services/evaluationTemplatesService.js';
import {
  DEFAULT_GRADER_MODEL,
  GRADER_MODEL_VARIABLE,
} from '../../../../services/grader.interface.js';
import { useScenarioFactory } from '../../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { useUserBi } from '../../../../telemetry/hooks/useUserBi/useUserBi.js';
import { ScenarioName } from '../../../../telemetry/scenarios.js';
import {
  ActionOutcome,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
  PanelType,
} from '../../../../telemetry/userbi.js';
import type { EvaluationRun } from '../../../../types/evaluationRun.js';
import {
  EVALUATION_TEMPLATE_NAME_MAX_LENGTH,
  isValidEvaluationTemplateName,
  type UpsertEvaluationTemplateBody,
} from '../../../../types/evaluationTemplate.js';
import { JobResultDialog } from '../../../EvaluationTemplatesPage/EvaluationTemplateDetailView/JobResultDialog.js';
import {
  addBindingFlowError,
  analyzeBindingFlow,
  applyGraderModelFallback,
  collectBindingVariables,
  variableHasDefault,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import type { BindingFlowResource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import { applyBindingsToParts } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import { defaultBindingFor } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import { seedFormStateFromTemplate } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.seed.js';
import type {
  EvaluationTemplateWithConfig,
  SeededFormState,
  TaskDraft,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import {
  buildTemplateBody,
  makeTask,
  parseInlineItems,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.utils.js';
import { getFormValidity } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.validation.js';
import { useBindingRows } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useBindingRows.js';
import { useEvaluationTemplateCatalogs } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useEvaluationTemplateCatalogs.js';
import { useFileSource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFileSource.js';
import { useFormFields } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFormFields.js';
import { useTaskDrafts } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useTaskDrafts.js';

import { useStyles } from './AgentEvaluateTab.style.js';
import { EvaluateFormColumn } from './EvaluateFormColumn.js';
import { EvaluateJobsRail } from './EvaluateJobsRail.js';

const TEMPLATE_NAME_PREFIX = 'agent-eval-';

// Stable 32-bit hash (djb2) of the original agent name, base36-encoded so it
// fits the `[a-z0-9-]` template-name charset. Used to disambiguate names that
// would otherwise truncate to the same backing-template name.
function hashSuffix(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  // >>> 0 → unsigned; base36 keeps it within [0-9a-z].
  return (hash >>> 0).toString(36);
}

// Deterministic, pattern-valid backing-template name for an agent. Agent names
// already follow the same `^[a-z][a-z0-9-]*$` shape, but we sanitize + cap
// length defensively so a stray character never produces an invalid name.
// When the name is too long to fit, we append a hash of the *full* name rather
// than plain-truncating, so two distinct long names that share a prefix don't
// collapse to the same template (which create-or-replace would silently
// overwrite, sharing a jobs rail).
export function backingTemplateName(agentName: string): string {
  const sanitized =
    agentName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+/, '') || 'agent';
  const available =
    EVALUATION_TEMPLATE_NAME_MAX_LENGTH - TEMPLATE_NAME_PREFIX.length;
  if (sanitized.length <= available) {
    return `${TEMPLATE_NAME_PREFIX}${sanitized}`;
  }
  const suffix = hashSuffix(agentName);
  const head = sanitized.slice(0, available - suffix.length - 1);
  return `${TEMPLATE_NAME_PREFIX}${head}-${suffix}`;
}

// Map a previously-saved backing template into the form's seed shape so
// re-entering the tab restores the last configuration instead of a blank form.
// GRADER_MODEL is stripped because this tab supplies it automatically via
// applyGraderModelFallback; surfacing it as an editable variable row would be
// confusing and could double-declare it on the next run.
export function seedFromBackingTemplate(
  template: EvaluationTemplateWithConfig,
): SeededFormState {
  const seeded = seedFormStateFromTemplate(template);
  return {
    ...seeded,
    variables: seeded.variables.filter(
      (variable) => variable.name !== GRADER_MODEL_VARIABLE,
    ),
  };
}

/**
 * Run controls the tab hands up to its parent so the primary action can live
 * in the agent's title bar (matching the Eval Template page, where "Queue job"
 * sits in the title bar rather than floating above the body).
 */
export interface AgentEvaluateControls {
  canRun: boolean;
  running: boolean;
  run: () => void;
}

export interface AgentEvaluateTabProps {
  workspaceName: string;
  agentName: string;
  /** Reports the run button's state up to the title bar; null when unmounting. */
  onControlsChange?: (controls: AgentEvaluateControls | null) => void;
}

// Outer wrapper: resolve this agent's durable backing template *before*
// mounting the form, so the form's useState seeds populate from the saved
// config on the first render. A missing template (first-time) seeds blank.
export function AgentEvaluateTab(props: AgentEvaluateTabProps) {
  const { workspaceName, agentName } = props;
  const styles = useStyles();
  const httpClient = useHttpClient();
  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );
  const templateName = useMemo(
    () => backingTemplateName(agentName),
    [agentName],
  );

  const [seedState, setSeedState] = useState<{
    loading: boolean;
    seeded: SeededFormState | null;
    templateName: string;
  }>({ loading: true, seeded: null, templateName });

  useEffect(() => {
    let cancelled = false;
    setSeedState({ loading: true, seeded: null, templateName });
    templatesService
      .get(workspaceName, templateName)
      .then((template) => {
        if (cancelled) return;
        setSeedState({
          loading: false,
          seeded: seedFromBackingTemplate(
            template as EvaluationTemplateWithConfig,
          ),
          templateName,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 404 is the expected first-time case: start with a blank form. Other
        // failures degrade to the same blank form (the user can reconfigure and
        // re-run), matching the prior always-blank behavior.
        if (!(err instanceof EvaluationTemplateNotFoundError)) {
          console.error('Failed to load backing evaluation template', err);
        }
        setSeedState({ loading: false, seeded: null, templateName });
      });
    return () => {
      cancelled = true;
    };
  }, [templatesService, workspaceName, templateName]);

  // Guard the stale window: when agentName changes, seedState still holds the
  // previous agent's seed until the effect above re-runs. Rendering the body
  // (keyed by the new templateName) with that stale seed would mount it with
  // the wrong backing config for one commit, so wait until the loaded seed
  // matches the current templateName.
  if (seedState.loading || seedState.templateName !== templateName) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading evaluation…" />
      </div>
    );
  }

  // Key by templateName so switching agents remounts the body with fresh seeds.
  return (
    <AgentEvaluateTabBody
      key={templateName}
      {...props}
      seeded={seedState.seeded}
    />
  );
}

interface AgentEvaluateTabBodyProps extends AgentEvaluateTabProps {
  seeded: SeededFormState | null;
}

function AgentEvaluateTabBody({
  workspaceName,
  agentName,
  onControlsChange,
  seeded,
}: AgentEvaluateTabBodyProps) {
  const styles = useStyles();
  const httpClient = useHttpClient();

  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );
  const runsService = useMemo(
    () => new EvaluationRunsService(httpClient),
    [httpClient],
  );
  const scenarioFactory = useScenarioFactory();
  const { recordAction } = useUserBi();

  const templateName = useMemo(
    () => backingTemplateName(agentName),
    [agentName],
  );

  // ── Form fields (query/data set, concurrency, variables) ──────────────
  const fields = useFormFields(seeded);

  // Single task with the agent pinned to this page's agent.
  const initialDrafts = useMemo(() => {
    const task = makeTask('default');
    return [{ ...task, agent: { ...task.agent, name: agentName } }];
  }, [agentName]);

  // Always re-pin the primary task to this page's agent. The seeded backing
  // template is a first-class eval template that can be edited elsewhere (e.g.
  // the Eval Templates page), so its persisted task agent may differ from this
  // tab's agentName; the Evaluate tab is contractually pinned to this agent.
  const draftsForHook = useMemo<TaskDraft[]>(() => {
    const drafts = seeded?.taskDrafts ?? initialDrafts;
    return drafts.map((draft, index) =>
      index === 0
        ? { ...draft, agent: { ...draft.agent, name: agentName } }
        : draft,
    );
  }, [agentName, initialDrafts, seeded?.taskDrafts]);

  const { taskDrafts, addGraderRow, removeGraderRow } = useTaskDrafts({
    initialDrafts: draftsForHook,
  });
  const primaryTask = taskDrafts[0]!;
  const selectedAgentNames = useMemo(() => [agentName], [agentName]);
  const selectedGraderNames = useMemo(
    () =>
      primaryTask.graderRows
        .map((grader) => grader.name)
        .filter((name) => name.length > 0),
    [primaryTask.graderRows],
  );

  // ── Catalogs (graders + the pinned agent) ─────────────────────────────
  const { uniqueGraders, gradersLoading, agents, agentsLoading, catalogError } =
    useEvaluationTemplateCatalogs({
      viewOnly: false,
      activeWorkspaceName: workspaceName,
      selectedAgentNames,
      selectedGraderNames,
    });
  const resolvedTaskDrafts = useMemo(
    () =>
      taskDrafts.map((task) => {
        const agent = agents.find(
          (candidate) => candidate.name === task.agent.name,
        );
        return {
          ...task,
          agent: {
            ...task.agent,
            version: agent?.version ?? task.agent.version,
          },
          graderRows: task.graderRows.map((graderRow) => {
            const grader = uniqueGraders.find(
              (candidate) => candidate.name === graderRow.name,
            );
            return {
              ...graderRow,
              version: grader?.version ?? graderRow.version,
            };
          }),
        };
      }),
    [agents, taskDrafts, uniqueGraders],
  );
  // ── File-mode pickers (query + data set) ──────────────────────────────
  const queryFile = useFileSource();
  const dataFile = useFileSource();

  const querySetItems = useMemo<string[] | null>(() => {
    if (fields.querySetMode === 'inline') {
      return parseInlineItems(fields.queryInlineText);
    }
    if (fields.querySetMode === 'file') return queryFile.items;
    return null;
  }, [fields.querySetMode, fields.queryInlineText, queryFile.items]);

  const dataSetItems = useMemo<string[] | null>(() => {
    if (!fields.dataSetEnabled) return null;
    if (fields.dataSetMode === 'inline') {
      return parseInlineItems(fields.dataInlineText);
    }
    if (fields.dataSetMode === 'file') return dataFile.items;
    return null;
  }, [
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.dataInlineText,
    dataFile.items,
  ]);

  const selectedResources = useMemo<BindingFlowResource[]>(() => {
    const resources: BindingFlowResource[] = [];
    const agent = agents.find((candidate) => candidate.name === agentName);
    if (agent !== undefined) {
      resources.push({
        kind: 'Agent',
        name: agent.name,
        version: agent.version,
        spec: agent.spec,
        variables: agent.variables,
      });
    }
    for (const graderRow of primaryTask.graderRows) {
      const grader = uniqueGraders.find(
        (candidate) => candidate.name === graderRow.name,
      );
      if (grader !== undefined) {
        resources.push({
          kind: 'Grader',
          name: grader.name,
          version: grader.version,
          spec: grader.spec,
          variables: grader.variables,
        });
      }
    }
    return resources;
  }, [agentName, agents, primaryTask.graderRows, uniqueGraders]);
  const {
    resources: bindingResources,
    shouldDeclare: shouldSupplyGraderModel,
  } = useMemo(
    () => applyGraderModelFallback(selectedResources, fields.variables),
    [fields.variables, selectedResources],
  );

  const bindingVariables = useMemo(
    () => collectBindingVariables(fields.variables, bindingResources),
    [bindingResources, fields.variables],
  );
  const getDefaultBinding = useCallback(
    (name: string) =>
      variableHasDefault(name, fields.variables, bindingResources)
        ? ''
        : defaultBindingFor(name),
    [bindingResources, fields.variables],
  );

  // ── Run + jobs state ──────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resultRunId, setResultRunId] = useState<string | null>(null);

  const [runHistory, setRunHistory] = useState<EvaluationRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const { bindingRows, setBinding } = useBindingRows(bindingVariables, {
    getDefault: getDefaultBinding,
    initialOverrides: seeded?.bindings,
  });
  const bindingFlow = useMemo(() => {
    let analysis = analyzeBindingFlow({
      resources: bindingResources,
      templateVariables: fields.variables,
      bindings: bindingRows,
      queryItems: querySetItems,
      dataItems: dataSetItems,
      dataSetEnabled: fields.dataSetEnabled,
      dataSetInspectable: fields.dataSetMode !== 'existing',
    });
    if (catalogError !== null && catalogError !== undefined) {
      analysis = addBindingFlowError(
        analysis,
        'catalog-resolution',
        `Unable to resolve selected component snapshots: ${catalogError}`,
      );
    }
    if (
      !agentsLoading &&
      !gradersLoading &&
      (catalogError === null || catalogError === undefined)
    ) {
      if (!agents.some((agent) => agent.name === agentName)) {
        analysis = addBindingFlowError(
          analysis,
          'missing-agent',
          `Agent ${agentName} could not be resolved in this workspace.`,
        );
      }
      for (const graderRow of primaryTask.graderRows) {
        if (
          graderRow.name.length > 0 &&
          !uniqueGraders.some((grader) => grader.name === graderRow.name)
        ) {
          analysis = addBindingFlowError(
            analysis,
            `missing-grader-${graderRow.name}`,
            `Grader ${graderRow.name} could not be resolved in this workspace.`,
          );
        }
      }
    }
    return analysis;
  }, [
    agentName,
    agents,
    agentsLoading,
    bindingResources,
    bindingRows,
    dataSetItems,
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.variables,
    querySetItems,
    catalogError,
    gradersLoading,
    primaryTask.graderRows,
    uniqueGraders,
  ]);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const runs = await runsService.list(workspaceName, {
        displayNamePrefix: templateName,
        top: 25,
      });
      setRunHistory(runs.filter((r) => r.displayName === templateName));
    } catch (err) {
      setHistoryError(
        err instanceof Error ? err.message : 'Failed to load runs.',
      );
      setRunHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [workspaceName, templateName, runsService]);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  const { canSubmit } = getFormValidity({
    jobName: templateName,
    taskDrafts: resolvedTaskDrafts,
    variables: fields.variables,
    querySetMode: fields.querySetMode,
    querySetItems,
    dataSetEnabled: fields.dataSetEnabled,
    dataSetMode: fields.dataSetMode,
    dataSetExistingId: fields.dataSetExistingId,
    dataSetFileSelector: fields.dataSetFileSelector,
    dataSetItems,
    bindingFlowHasErrors:
      bindingFlow.hasErrors || agentsLoading || gradersLoading,
    submitting: running,
    workspaceLoading: false,
    activeWorkspaceName: workspaceName,
  });

  const selectedGraderCount = primaryTask.graderRows.filter(
    (r) => r.name.length > 0,
  ).length;
  // The whole point of this tab is "evaluate with a grader selected", so —
  // unlike the general eval-template form — at least one grader is required.
  const canRun =
    canSubmit &&
    selectedGraderCount > 0 &&
    isValidEvaluationTemplateName(templateName);

  const handleRun = useCallback(async () => {
    if (!canRun) return;
    setRunError(null);
    setRunning(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.AgentEvaluationSubmit,
    );
    try {
      const parts = applyBindingsToParts(
        buildTemplateBody({
          taskDrafts: resolvedTaskDrafts,
          variables: fields.variables,
          querySetItems,
          dataSetEnabled: fields.dataSetEnabled,
          dataSetMode: fields.dataSetMode,
          dataSetItems,
          dataSetExistingId: fields.dataSetExistingId,
          dataSetExistingVersion: fields.dataSetExistingVersion,
          dataSetFileSelector: fields.dataSetFileSelector,
          workersText: fields.workersText,
          parallelism: fields.parallelism,
        }),
        bindingRows,
      );
      const variables = shouldSupplyGraderModel
        ? {
            ...parts.variables,
            [GRADER_MODEL_VARIABLE]: {
              type: 'string',
              default: DEFAULT_GRADER_MODEL,
            },
          }
        : parts.variables;
      // PUT (create-or-replace) the backing template, then queue a run. The
      // run's displayName is the template name so the Jobs rail can filter
      // this agent's runs out of the workspace-wide list.
      const body = {
        displayName: `Evaluate ${agentName}`,
        ...parts,
        variables,
        ...(Object.keys(variables).length === 0
          ? { variables: undefined }
          : {}),
      } as unknown as UpsertEvaluationTemplateBody;
      const template = await templatesService.replace(
        workspaceName,
        templateName,
        body,
      );
      scenario.mark('template_saved');
      // Hosted runs resolve the template's latest *snapshot*, never the working
      // copy. A replace on an existing template marks it a draft, so without
      // this the run would silently evaluate the previous snapshot. Snapshot
      // only when the PUT reported a draft — a first create already equals v1,
      // so this avoids minting a redundant duplicate version.
      if (template.isDraft) {
        await templatesService.snapshot(workspaceName, templateName);
        scenario.mark('template_snapshotted');
      }
      const run = await runsService.queue(workspaceName, {
        runOrigin: 'hosted',
        templateName,
        displayName: templateName,
      });
      scenario.mark('run_queued');
      scenario.complete('success');
      setRunHistory((prev) => [run, ...prev]);
      void refreshHistory();
    } catch (err) {
      scenario.complete('failure');
      setRunError(
        err instanceof Error ? err.message : 'Failed to start evaluation.',
      );
    } finally {
      setRunning(false);
    }
  }, [
    canRun,
    agentName,
    workspaceName,
    templateName,
    templatesService,
    runsService,
    scenarioFactory,
    refreshHistory,
    resolvedTaskDrafts,
    querySetItems,
    dataSetItems,
    bindingRows,
    shouldSupplyGraderModel,
    // Depend on the individual field values handleRun reads, not the whole
    // `fields` object — useFormFields returns a fresh object each render, so
    // depending on it would recreate handleRun every render and (via the
    // controls effect) drive an unbounded parent re-render loop.
    fields.variables,
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.dataSetExistingId,
    fields.dataSetExistingVersion,
    fields.dataSetFileSelector,
    fields.workersText,
    fields.parallelism,
  ]);

  const handleCancelRun = useCallback(
    async (runId: string) => {
      setRunError(null);
      setCancellingId(runId);
      try {
        recordAction({
          panelType: PanelType.AgentsPage,
          panelRegion: PanelRegion.Main,
          actionScenario: getOperationActionScenario(
            EntityType.EvaluationRun,
            OperationType.Cancel,
          ),
          actionScenarioType: OperationType.Cancel,
          actionGesture: 'click',
          actionOutcome: ActionOutcome.Requested,
        });
        const updated = await runsService.cancel(workspaceName, runId);
        setRunHistory((prev) =>
          prev.map((r) => (r.id === updated.id ? updated : r)),
        );
      } catch (err) {
        setRunError(
          err instanceof Error ? err.message : 'Failed to cancel run.',
        );
      } finally {
        setCancellingId((prev) => (prev === runId ? null : prev));
      }
    },
    [workspaceName, runsService, recordAction],
  );

  // Surface the run button's state to the title bar. Re-reports whenever the
  // gate or handler changes; clears on unmount (tab switch) so the title bar
  // drops the button.
  useEffect(() => {
    onControlsChange?.({ canRun, running, run: () => void handleRun() });
  }, [onControlsChange, canRun, running, handleRun]);
  useEffect(() => () => onControlsChange?.(null), [onControlsChange]);

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <EvaluateFormColumn
          agentName={agentName}
          disabled={running}
          fields={fields}
          bindings={bindingRows}
          bindingFlow={bindingFlow}
          onBindingChange={setBinding}
          queryFile={queryFile}
          dataFile={dataFile}
          task={primaryTask}
          uniqueGraders={uniqueGraders}
          gradersLoading={gradersLoading}
          onAddGraderRow={(name) => addGraderRow(primaryTask.id, name)}
          onRemoveGraderRow={(rowId) => removeGraderRow(primaryTask.id, rowId)}
          runError={runError}
        />

        <EvaluateJobsRail
          runHistory={runHistory}
          loading={historyLoading}
          error={historyError}
          cancellingId={cancellingId}
          onCancelRun={(runId) => void handleCancelRun(runId)}
          onOpenResult={setResultRunId}
        />
      </div>

      <JobResultDialog
        workspaceName={workspaceName}
        runId={resultRunId}
        runsService={runsService}
        onClose={() => setResultRunId(null)}
      />
    </div>
  );
}
